/**
 * Pure helpers for the Manage backfill flow — turning a leaver's Personio record into the propose
 * form's starting values. Kept out of the components for the same reason as `proposeRole.ts`.
 *
 * Every vocabulary field (Role, Department, Unit, Track and Level) is kept only when the Airtable
 * choice list actually offers it. A Personio value the list does not know is left EMPTY and named
 * in `unmatched`, never forced in: the create call sends no `typecast`, so a stray value would only
 * come back as an Airtable error on submit.
 */
import type {
  BackfillDraft,
  ProposalFieldOptions,
  ProposedRoleInput,
} from '../../services/internalPlaygroundApi';
import type { HiringManagerOption } from './proposeRole';

export const BACKFILL_REASON = 'Backfill';

/** The leaver a backfill replaces, as the propose form needs it. */
export interface BackfillTarget {
  employeeId: string;
  name: string;
  terminationDate: string | null;
  draft: BackfillDraft;
}

/** `value` if `choices` offers it — exactly, or ignoring case and surrounding space. */
export function matchChoice(value: string | null | undefined, choices: string[]): string {
  const wanted = (value ?? '').trim();
  if (!wanted) return '';
  if (choices.includes(wanted)) return wanted;
  return choices.find((c) => c.trim().toLowerCase() === wanted.toLowerCase()) ?? '';
}

export interface BackfillPrefill {
  form: ProposedRoleInput;
  /** Human labels of Personio values that had no matching Airtable choice. */
  unmatched: string[];
}

/**
 * The form a backfill starts from. With `options` still loading, the vocabulary fields stay empty
 * and nothing is reported as unmatched — the caller fills them in once the options arrive
 * (`fillBlanks`).
 *
 * Planned start date is always left open (David, Sep 2026). Reason for Hiring is always Backfill;
 * the proxy forces it too.
 */
export function backfillPrefill(
  target: BackfillTarget,
  options: ProposalFieldOptions | undefined,
  managers: HiringManagerOption[]
): BackfillPrefill {
  const { draft } = target;
  const unmatched: string[] = [];
  const vocab = (label: string, value: string | null, choices: string[] | undefined) => {
    if (!options || !value) return '';
    const hit = matchChoice(value, choices ?? []);
    if (!hit) unmatched.push(`${label} "${value}"`);
    return hit;
  };

  const hiringManagerEmployeeId =
    draft.supervisorId && managers.some((m) => m.employeeId === draft.supervisorId) ? draft.supervisorId : '';

  return {
    form: {
      title: draft.position ?? '',
      role: vocab('Role', draft.role, options?.role),
      peopleTeam: vocab('Department', draft.department, options?.peopleTeam),
      hiringManagerEmployeeId,
      plannedStartDate: '',
      proposalRational: '',
      unit: vocab('Unit', draft.team, options?.unit),
      trackAndLevel: vocab('Track and level', draft.suggestedTrackAndLevel, options?.trackAndLevel),
      reasonForHiring: BACKFILL_REASON,
      costCategory: options ? matchChoice(draft.costCategory, options.costCategory ?? []) : draft.costCategory,
      backfillForEmployeeId: target.employeeId,
    },
    unmatched,
  };
}

/** Fill only the fields still blank in `current` — so a field the proposer already touched while
 * the options were loading is not overwritten when they arrive. */
export function fillBlanks(current: ProposedRoleInput, next: ProposedRoleInput): ProposedRoleInput {
  const merged = { ...current };
  for (const key of Object.keys(next) as (keyof ProposedRoleInput)[]) {
    if (!(current[key] ?? '').trim() && next[key]) merged[key] = next[key];
  }
  return merged;
}

/**
 * The note under the form explaining the preselected level (David, Sep 2026): backfills go in at
 * least one level below the person they replace, and an exception is argued in "Why this role?".
 */
export function levelNote(target: BackfillTarget): string {
  const { trackAndLevel, suggestedTrackAndLevel } = target.draft;
  const rule =
    'We backfill at least one level below the previous person holding the role. To propose an exception, outline it in “Why this role?”.';
  if (!trackAndLevel || !suggestedTrackAndLevel) return rule;
  if (trackAndLevel === suggestedTrackAndLevel) {
    return `${target.name} is on ${trackAndLevel}, which we do not go below, so the level stays the same. ${rule}`;
  }
  return `${target.name} is on ${trackAndLevel}, so ${suggestedTrackAndLevel} is preselected. ${rule}`;
}

/** 'YYYY-MM-DD' → '5 Jan 2027'. Parsed as a calendar day, never through a timezone. */
export function formatDay(date: string | null | undefined): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date ?? '');
  if (!match) return '';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${Number(match[3])} ${months[Number(match[2]) - 1]} ${match[1]}`;
}
